import React from 'react';
import { motion } from 'framer-motion';
import { 
  BookOpen,
  Users,
  Award,
  Clock,
  MapPin,
  Monitor,
  CheckCircle,
  ArrowRight,
  GraduationCap,
  Target
} from 'lucide-react';

const Training = () => {
  const courses = [
    {
      title: 'Reliability Engineering Fundamentals',
      duration: '3 Days',
      format: 'In-Person / Virtual',
      level: 'Beginner to Intermediate',
      description: 'Comprehensive foundation in reliability engineering principles, methodologies, and best practices.',
      modules: [
        'Introduction to Reliability Engineering',
        'Failure Modes and Effects Analysis (FMEA)',
        'Reliability Centered Maintenance (RCM)',
        'Statistical Analysis and Modeling',
        'Life Cycle Cost Analysis',
        'Case Studies and Practical Applications'
      ],
      certification: 'AAMS Reliability Engineering Certificate',
      prerequisites: 'Basic engineering background',
      image: '/images/training.png'
    },
    {
      title: 'Condition Monitoring Specialist',
      duration: '5 Days',
      format: 'Hands-On Workshop',
      level: 'Intermediate to Advanced',
      description: 'Advanced training in condition monitoring techniques including vibration analysis, thermal imaging, and oil analysis.',
      modules: [
        'Vibration Analysis Fundamentals',
        'Thermal Imaging Techniques',
        'Oil Analysis Interpretation',
        'Acoustic Emission Monitoring',
        'Data Collection Best Practices',
        'Diagnostic Techniques and Tools'
      ],
      certification: 'ISO Category II Vibration Analysis',
      prerequisites: 'Maintenance or engineering experience',
      image: '/images/training.png'
    },
    {
      title: 'Predictive Maintenance Strategy',
      duration: '2 Days',
      format: 'Workshop / Virtual',
      level: 'All Levels',
      description: 'Strategic approach to implementing and optimizing predictive maintenance programs for maximum ROI.',
      modules: [
        'Predictive Maintenance Fundamentals',
        'Technology Selection Criteria',
        'Implementation Planning',
        'ROI Analysis and Justification',
        'Program Management',
        'Continuous Improvement Strategies'
      ],
      certification: 'Predictive Maintenance Strategist',
      prerequisites: 'None',
      image: '/images/training.png'
    },
    {
      title: 'Root Cause Analysis Workshop',
      duration: '2 Days',
      format: 'Interactive Workshop',
      level: 'All Levels',
      description: 'Practical training in systematic problem-solving methodologies for identifying and eliminating equipment failures.',
      modules: [
        'RCA Methodologies Overview',
        'Data Collection Techniques',
        'Cause and Effect Analysis',
        'Fault Tree Analysis',
        'Human Factors in Failures',
        'Action Plan Development'
      ],
      certification: 'RCA Practitioner Certificate',
      prerequisites: 'None',
      image: '/images/training.png'
    },
    {
      title: 'Asset Management Excellence',
      duration: '4 Days',
      format: 'Comprehensive Program',
      level: 'Management / Leadership',
      description: 'Strategic asset management program for leadership teams focusing on organizational excellence and performance optimization.',
      modules: [
        'Asset Management Strategy',
        'ISO 55000 Standards',
        'Performance Measurement',
        'Risk Management',
        'Organizational Alignment',
        'Change Management'
      ],
      certification: 'Asset Management Leader',
      prerequisites: 'Management experience',
      image: '/images/training.png'
    },
    {
      title: 'IoT and Digital Technologies',
      duration: '2 Days',
      format: 'Technical Workshop',
      level: 'Intermediate',
      description: 'Hands-on training in Industrial IoT implementation, data analytics, and digital transformation for maintenance.',
      modules: [
        'Industrial IoT Fundamentals',
        'Sensor Technologies',
        'Data Analytics and AI',
        'Integration Strategies',
        'Cybersecurity Considerations',
        'Digital Transformation Roadmap'
      ],
      certification: 'Industrial IoT Specialist',
      prerequisites: 'Technical background',
      image: '/images/training.png'
    }
  ];

  const trainingFeatures = [
    {
      icon: GraduationCap,
      title: 'Expert Instructors',
      description: 'Industry veterans with 15+ years of hands-on experience'
    },
    {
      icon: Target,
      title: 'Practical Approach',
      description: 'Real-world case studies and hands-on exercises'
    },
    {
      icon: Award,
      title: 'Industry Certification',
      description: 'Recognized certificates to advance your career'
    },
    {
      icon: Users,
      title: 'Flexible Formats',
      description: 'In-person, virtual, and custom on-site options'
    }
  ];

  const benefits = [
    'Reduce equipment downtime by up to 50%',
    'Improve maintenance efficiency and productivity',
    'Enhance safety and regulatory compliance',
    'Build in-house technical expertise',
    'Achieve measurable ROI from maintenance investments',
    'Stay current with industry best practices and technologies'
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
              Build In-House <span className="text-[#233790]">Reliability Champions</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Empower your team with industry-leading training programs in reliability engineering, 
              condition monitoring, and asset management best practices.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Training Features */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trainingFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-[#233790] to-[#3BA6EA] rounded-lg flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                Training Benefits & Outcomes
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our training programs deliver measurable results and lasting impact on your 
                organization's maintenance and reliability performance.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-[#233790] mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.pexels.com/photos/3184430/pexels-photo-3184430.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Training Session"
                className="w-full rounded-lg shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#233790]/10 to-transparent rounded-lg"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Custom Training */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Custom Training Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored training programs designed specifically for your organization's needs, 
              equipment, and operational challenges.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-lg p-8 text-center shadow-sm"
            >
              <MapPin className="w-12 h-12 text-[#233790] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-4">On-Site Training</h3>
              <p className="text-gray-600">
                Bring our experts to your facility for hands-on training with your actual equipment and processes.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-lg p-8 text-center shadow-sm"
            >
              <BookOpen className="w-12 h-12 text-[#233790] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Curriculum Design</h3>
              <p className="text-gray-600">
                Custom curriculum development based on your specific industry requirements and skill gaps.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-lg p-8 text-center shadow-sm"
            >
              <Users className="w-12 h-12 text-[#233790] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Team Development</h3>
              <p className="text-gray-600">
                Comprehensive team development programs with ongoing support and mentoring.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Invest in Your Team's Success
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Build the reliability expertise your organization needs to achieve operational excellence 
              and sustainable competitive advantage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              
              <a
                href="https://aswarthagroup.com/training/"
                className="inline-flex items-center px-8 py-3 border border-[#233790] text-[#233790] font-medium rounded-lg hover:bg-[#F0F4FE] transition-colors"
              >
                Request Custom Training <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Training;